// ─── hooks/useAppData.js ──────────────────────────────────────────────────────
// Couche données centralisée extraite de App.js.
// Prend le `user` brut de useAuth() et calcule resolvedUser en interne.
// App.js ne garde que : auth, routing, UI state, layout, notifications.
// ─────────────────────────────────────────────────────────────────────────────
import { useMemo } from 'react';
import { useDB } from './useDB';
import { usePresences } from '../components/modules/Presences';
import {
  USERS, SAMPLE_STAFF, SAMPLE_EVENTS, SAMPLE_CONGES, SAMPLE_SOLDES,
  SAMPLE_DOCS, SAMPLE_MEDECINE, SAMPLE_ENTRETIENS, SAMPLE_BASEDOCS,
  SAMPLE_COMM_PERMS, SAMPLE_ANNONCES, SAMPLE_MESSAGES,
  SAMPLE_PROFILS, SAMPLE_MODELES_CONTRATS, SAMPLE_CONTRATS_GEN,
  SAMPLE_PLANNING_H, SAMPLE_POSTES,
  SAMPLE_REMUNERATIONS, SAMPLE_ABSENCES_NP, SAMPLE_FORMATIONS,
  SAMPLE_BENEVOLES, SAMPLE_ONBOARDING,
} from '../data/constants';
import {
  SAMPLE_AFFICHAGE, SAMPLE_PLAN_FORMATION, SAMPLE_RUP, SAMPLE_COMPTA_EXERCICES,
  SAMPLE_ALERTES_CONFIG, SAMPLE_NOTES_RH, SAMPLE_MESSAGERIE, SAMPLE_OBJECTIFS,
  SAMPLE_WORKFLOWS, SAMPLE_DEMANDES_WF, SAMPLE_INFOS_ASSO,
  SAMPLE_ADHERENTS, SAMPLE_ADHERENTS_CONFIG, SAMPLE_VARIABLES_PAIE,
} from '../data/sampleData';

export function useAppData(user) {
  // ── Menu & personnalisation ────────────────────────────────────────────────
  const [groupsOrder,   setGroupsOrder]   = useDB('cmob_menu_order',    []);
  const [menuConfig,    setMenuConfig]    = useDB('cmob_menu_config',    {});
  const [menuHidden,    setMenuHidden]    = useDB('cmob_menu_hidden',    []);
  const [menuLabels,    setMenuLabels]    = useDB('cmob_menu_labels',    {});
  const [hubConfig,     setHubConfig]     = useDB('cmob_hub_config',     {});
  const [modulesDesac,  setModulesDesac]  = useDB('cmob_modules_desac',  []);
  const [moduleRoles]                     = useDB('cmob_module_roles',   {});
  const [chatbotConfig, setChatbotConfig] = useDB('cmob_chatbot_acces',  { actif:true, exceptions:{} });
  const [orgaConfig,    setOrgaConfig]    = useDB('cmob_orga_config',    {});

  // ── Config ─────────────────────────────────────────────────────────────────
  const [paramsAPI, setParamsAPI] = useDB('cmob_params_api', {});
  const [paramsRH,  setParamsRH]  = useDB('cmob_params_rh',  {});

  // ── Données RH ─────────────────────────────────────────────────────────────
  const [staff,         setStaff]         = useDB('cmob_staff',           SAMPLE_STAFF);
  const [postes,        setPostes]        = useDB('cmob_postes',          SAMPLE_POSTES);
  const [rup,           setRup]           = useDB('cmob_rup',             SAMPLE_RUP);
  const [events,        setEvents]        = useDB('cmob_events',          SAMPLE_EVENTS);
  const [conges,        setConges]        = useDB('cmob_conges',          SAMPLE_CONGES);
  const [soldes,        setSoldes]        = useDB('cmob_soldes',          SAMPLE_SOLDES);
  const [docs,          setDocs]          = useDB('cmob_docs',            SAMPLE_DOCS);
  const [medecine,      setMedecine]      = useDB('cmob_medecine',        SAMPLE_MEDECINE);
  const [entretiens,    setEntretiens]    = useDB('cmob_entretiens',      SAMPLE_ENTRETIENS);
  const [baseDocs,      setBaseDocs]      = useDB('cmob_basedocs',        SAMPLE_BASEDOCS);
  const [users,         setUsers,
         loadingUsers,  syncedUsers]      = useDB('cmob_users',           USERS);
  const [planningH,     setPlanningH]     = useDB('cmob_planning_h',      SAMPLE_PLANNING_H);
  const [profils,       setProfils]       = useDB('cmob_profils',         SAMPLE_PROFILS);
  const [modelesContrats, setModelesContrats] = useDB('cmob_modeles_contrats', SAMPLE_MODELES_CONTRATS);
  const [contratsGen,   setContratsGen]   = useDB('cmob_contrats_gen',    SAMPLE_CONTRATS_GEN);
  const [commPerms,     setCommPerms]     = useDB('cmob_comm_perms',      SAMPLE_COMM_PERMS);
  const [annonces,      setAnnonces]      = useDB('cmob_annonces',        SAMPLE_ANNONCES);
  const [messages,      setMessages]      = useDB('cmob_messages',        SAMPLE_MESSAGES);
  const [remunerations, setRemunerations] = useDB('cmob_remunerations',   SAMPLE_REMUNERATIONS);
  const [absencesNP,    setAbsencesNP]    = useDB('cmob_absences_np',     SAMPLE_ABSENCES_NP);
  // formations géré par Formations.jsx — stub pour compatibilité props
  const formations    = SAMPLE_FORMATIONS;
  const setFormations = () => {};
  const [benevoles,     setBenevoles]     = useDB('cmob_benevoles',       SAMPLE_BENEVOLES);
  const [reunions,      setReunions]      = useDB('cmob_reunions',        []);
  // eslint-disable-next-line no-unused-vars
  const [_compta,       _setCompta]       = useDB('cmob_compta_exercices', SAMPLE_COMPTA_EXERCICES);
  const [onboarding,    setOnboarding]    = useDB('cmob_onboarding',      SAMPLE_ONBOARDING);
  const [alertesConfig, setAlertesConfig] = useDB('cmob_alertes_config',  SAMPLE_ALERTES_CONFIG);
  const [espaceSections]                  = useDB('cmob_espaces_sections', {});
  const [adherents,     setAdherents]     = useDB('cmob_adherents',       SAMPLE_ADHERENTS);
  const [adherentsConfig, setAdherentsConfig] = useDB('cmob_adherents_cfg', SAMPLE_ADHERENTS_CONFIG);
  // eslint-disable-next-line no-unused-vars
  const [sectionPerms,  setSectionPerms]  = useDB('cmob_section_perms',   {});
  const [accesCoordAdh, setAccesCoordAdh] = useDB('cmob_acces_coord_adh', {});
  const [varPaie,       setVarPaie]       = useDB('cmob_variables_paie',  SAMPLE_VARIABLES_PAIE);
  const [affichage,     setAffichage]     = useDB('cmob_affichage',       SAMPLE_AFFICHAGE);
  const [planFormation, setPlanFormation] = useDB('cmob_plan_formation',  SAMPLE_PLAN_FORMATION);
  const [notesRH,       setNotesRH]       = useDB('cmob_notes_rh',        SAMPLE_NOTES_RH);
  const [messagerie,    setMessagerie]    = useDB('cmob_messagerie',      SAMPLE_MESSAGERIE);
  const [objectifs,     setObjectifs]     = useDB('cmob_objectifs',       SAMPLE_OBJECTIFS);
  const [workflows,     setWorkflows]     = useDB('cmob_workflows',       SAMPLE_WORKFLOWS);
  const [demandesWF,    setDemandesWF]    = useDB('cmob_demandes_wf',     SAMPLE_DEMANDES_WF);
  const [infosAsso,     setInfosAsso]     = useDB('cmob_infos_asso',      SAMPLE_INFOS_ASSO);
  const [accesCoord]                      = useDB('cmob_acces_coordonnees', []);
  const [audit,         setAudit]         = useDB('cmob_audit',            []);
  const [connexions,    setConnexions]    = useDB('cmob_connexions',       []);
  const [presences,     setPresences]     = usePresences();

  // ── resolvedUser ───────────────────────────────────────────────────────────
  const resolvedUser = useMemo(
    () => user && users.length
      ? (users.find(u => String(u.id) === String(user.id)) || user)
      : user,
    [user, users]
  );

  // ── Rôles ──────────────────────────────────────────────────────────────────
  const userRoles     = resolvedUser
    ? (resolvedUser.roles || (resolvedUser.role ? [resolvedUser.role] : []))
    : [];
  const hasRole        = r => userRoles.includes(r);
  const isAdmin        = hasRole('admin');
  const isRH           = hasRole('rh') || isAdmin;
  const isManager      = hasRole('manager');
  const isBureau       = hasRole('bureau');
  const isCodir        = hasRole('codir');
  const isSal          = hasRole('salarie');
  // eslint-disable-next-line no-unused-vars
  const peutVoirBureau = isBureau || isCodir || isRH || isAdmin ||
    (resolvedUser?.bureau_droits?.voir === true);
  const myStaffId = resolvedUser?.staffId;
  const myUserId  = resolvedUser?.id;

  // ── Badges nav congés ─────────────────────────────────────────────────────
  const congesEnAttenteMgr = useMemo(
    () => isManager
      ? conges.filter(c => {
          const idx = (c.managers||[]).findIndex(m => String(m.staffId) === String(myStaffId));
          if (idx < 0) return false;
          return c.statut === `En attente M${idx + 1}`;
        }).length
      : 0,
    [isManager, conges, myStaffId] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const congesEnAttenteRH = useMemo(
    () => isRH
      ? conges.filter(c => {
          if (['Approuvé RH','Approuve RH','APPROUVÉ','Refusé RH','Refuse RH'].includes(c.statut)) return false;
          const nbM = (c.managers||[]).length;
          if (nbM === 0) return c.statut === 'En attente RH';
          if (nbM === 1) return c.statut === 'Valide M1';
          return c.statut === 'Valide M2';
        }).length
      : 0,
    [isRH, conges] // eslint-disable-line react-hooks/exhaustive-deps
  );

  // ── Chatbot ────────────────────────────────────────────────────────────────
  const chatbotActif   = chatbotConfig?.actif !== false;
  const exceptionUser  = chatbotConfig?.exceptions?.[user?.id];
  const niveauChatbot  = exceptionUser && exceptionUser !== 'defaut'
    ? exceptionUser
    : (isAdmin ? 'rh' : isRH ? 'rh' : isManager ? 'manager' : 'salarie');
  const chatbotVisible = chatbotActif && niveauChatbot !== 'aucun';
  const chatIsAdmin    = niveauChatbot === 'rh' && isAdmin;
  const chatIsRH       = niveauChatbot === 'rh';
  const chatIsManager  = niveauChatbot === 'manager' || chatIsRH;

  // ── Audit helpers ──────────────────────────────────────────────────────────
  const logAction = entry => {
    const e = { id: Date.now()+Math.random(), date: new Date().toISOString(), ...entry };
    setAudit(prev => [e, ...(Array.isArray(prev) ? prev : [])].slice(0, 500));
  };

  const logConnexion = event =>
    setConnexions(prev => [...(prev||[]), { ...event, id: Date.now() }].slice(-500));

  const mkAuditSet = (setter, module) => (fn, action, label, avant=null) => {
    setter(prev => {
      const next = typeof fn === 'function' ? fn(prev) : fn;
      if (resolvedUser) logAction({
        userNom: `${resolvedUser.prenom||''} ${resolvedUser.nom||''}`.trim() || resolvedUser.login,
        module, action, entiteLabel: label, avant,
        apres: action === 'suppression'
          ? null
          : (Array.isArray(next) ? next.find(x => x.id === (avant?.id)) : next),
      });
      return next;
    });
  };

  const auditSetFormations = mkAuditSet(setFormations, 'Formations');
  const auditSetMedecine   = mkAuditSet(setMedecine,   'Médecine du travail');
  const auditSetDocs       = mkAuditSet(setDocs,       'Documents');
  const auditSetBenevoles  = mkAuditSet(setBenevoles,  'Bénévoles');
  const auditSetRup        = mkAuditSet(setRup,        'RUP');

  return {
    // Menu
    groupsOrder, setGroupsOrder, menuConfig, setMenuConfig,
    menuHidden, setMenuHidden, menuLabels, setMenuLabels,
    hubConfig, setHubConfig, modulesDesac, setModulesDesac,
    moduleRoles, chatbotConfig, setChatbotConfig, orgaConfig, setOrgaConfig,
    // Config
    paramsAPI, setParamsAPI, paramsRH, setParamsRH,
    // Données
    staff, setStaff, postes, setPostes, rup, setRup,
    events, setEvents, conges, setConges, soldes, setSoldes,
    docs, setDocs, medecine, setMedecine, entretiens, setEntretiens,
    baseDocs, setBaseDocs, users, setUsers, loadingUsers, syncedUsers,
    planningH, setPlanningH, profils, setProfils,
    modelesContrats, setModelesContrats, contratsGen, setContratsGen,
    commPerms, setCommPerms, annonces, setAnnonces, messages, setMessages,
    remunerations, setRemunerations, absencesNP, setAbsencesNP,
    formations, setFormations, benevoles, setBenevoles, reunions, setReunions,
    onboarding, setOnboarding, alertesConfig, setAlertesConfig, espaceSections,
    adherents, setAdherents, adherentsConfig, setAdherentsConfig,
    accesCoordAdh, setAccesCoordAdh, varPaie, setVarPaie,
    affichage, setAffichage, planFormation, setPlanFormation,
    notesRH, setNotesRH, messagerie, setMessagerie,
    objectifs, setObjectifs, workflows, setWorkflows,
    demandesWF, setDemandesWF, infosAsso, setInfosAsso,
    presences, setPresences, audit, setAudit, connexions,
    accesCoord, sectionPerms,
    // Rôles
    resolvedUser, userRoles, hasRole,
    isAdmin, isRH, isManager, isBureau, isCodir, isSal, peutVoirBureau,
    myStaffId, myUserId,
    // Badges
    congesEnAttenteMgr, congesEnAttenteRH,
    // Chatbot
    chatbotVisible, chatIsAdmin, chatIsRH, chatIsManager, niveauChatbot,
    // Audit
    logAction, logConnexion, mkAuditSet,
    auditSetFormations, auditSetMedecine, auditSetDocs, auditSetBenevoles, auditSetRup,
  };
}
